import { DEFAULT_INVITE_DAYS, type Locale, MAX_INVITE_DAYS, pickLocale } from '@bb/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import {
  invitesQuery,
  useCreateInvite,
  useEventTypes,
  useRevokeInvite,
} from '@/features/booking/api';
import { ApiRequestError } from '@/lib/api';
import { btnPrimary, btnSmall, cardCls, inputCls } from '@/lib/ui';

interface InvitesSearch {
  eventTypeId?: string;
}

/**
 * Single-use booking links live here rather than inside the event type editor.
 *
 * Minting one happens every time an enquiry arrives; editing an event type
 * happens once. Putting a frequent action behind a configuration screen, with
 * a Save button that saves nothing, was the wrong way round.
 */
export const Route = createFileRoute('/app/booking/invites')({
  validateSearch: (s: Record<string, unknown>): InvitesSearch =>
    typeof s.eventTypeId === 'string' ? { eventTypeId: s.eventTypeId } : {},
  component: InvitesPage,
});

function InvitesPage() {
  const { t, i18n } = useTranslation(['booking', 'common', 'errors']);
  const locale = i18n.language as Locale;
  const { data: user } = useCurrentUser();
  const { data: eventTypes = [] } = useEventTypes();
  const { eventTypeId: fromSearch } = Route.useSearch();

  const [chosen, setChosen] = useState<string | null>(null);
  const eventTypeId = chosen ?? fromSearch ?? eventTypes[0]?.id ?? '';

  const { data } = useQuery({ ...invitesQuery(eventTypeId), enabled: Boolean(eventTypeId) });
  const create = useCreateInvite(eventTypeId);
  const revoke = useRevokeInvite(eventTypeId);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [days, setDays] = useState(DEFAULT_INVITE_DAYS);
  const [minted, setMinted] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fmt = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  const mint = () => {
    if (!eventTypeId) return;
    setError(null);
    setMinted(null);
    create.mutate(
      {
        eventTypeId,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        expiresInDays: days,
      },
      {
        onSuccess: (invite) => {
          setMinted(invite.url);
          setName('');
          setEmail('');
          // Straight onto the clipboard: the next thing they do is paste it.
          // The confirmation is cleared only once it has actually been shown,
          // rather than on a timer that starts before the write resolves.
          if (invite.url) {
            void navigator.clipboard
              .writeText(invite.url)
              .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
              })
              // Refused when the page is not focused, which some browsers do.
              // The link is on screen with its own copy button, so say nothing.
              .catch(() => undefined);
          }
        },
        onError: (err: unknown) => setError(err instanceof ApiRequestError ? err.code : 'generic'),
      },
    );
  };

  if (!user) return null;

  if (eventTypes.length === 0) {
    return <p className="text-sm text-neutral-500">{t('booking:empty.noEventType')}</p>;
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/*
        Arriving from an event type row is a detour from a task on that list,
        so offer the way back. Reaching the page from the nav is not a detour
        and needs no such link, hence keying off the search param rather than
        the current selection, which the select may since have changed.
      */}
      {fromSearch ? (
        <Link
          to="/app/booking"
          className="-ml-2 inline-flex min-h-11 items-center px-2 text-sm text-neutral-500 hover:text-neutral-900 sm:ml-0 sm:min-h-0 sm:px-0"
        >
          ‹ {t('booking:nav.eventTypes')}
        </Link>
      ) : null}

      <p className="text-sm text-neutral-600">{t('booking:invites.help')}</p>

      <section className={`${cardCls} space-y-4`}>
        <div>
          <span className="mb-1 block text-sm font-medium">{t('booking:invites.forEvent')}</span>
          <select
            value={eventTypeId}
            onChange={(e) => {
              setChosen(e.target.value);
              setMinted(null);
            }}
            className={inputCls}
          >
            {eventTypes.map((et) => (
              <option key={et.id} value={et.id}>
                {pickLocale(et.name, locale)}
                {et.visibility === 'invite_only' ? ` · ${t('booking:invites.inviteOnly')}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className="mb-1 block text-sm font-medium">{t('booking:invites.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <span className="mb-1 block text-sm font-medium">{t('booking:invites.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <span className="mb-1 block text-sm font-medium">{t('booking:invites.expiry')}</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={days}
                min={1}
                max={MAX_INVITE_DAYS}
                onChange={(e) => setDays(Number(e.target.value))}
                className={`${inputCls} sm:w-24`}
              />
              <span className="text-sm text-neutral-500">
                {t('booking:eventTypes.units.days', { count: days })}
              </span>
            </div>
          </div>
          <button type="button" onClick={mint} disabled={create.isPending} className={btnPrimary}>
            {t('booking:invites.create')}
          </button>
        </div>

        {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}

        {minted ? (
          <div className="rounded border border-green-200 bg-green-50 p-3">
            <p className="text-sm font-medium text-green-900">
              {copied ? t('booking:invites.copied') : t('booking:invites.ready')}
            </p>
            <p className="mt-1 text-xs text-green-800">{t('booking:invites.onceOnly')}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded border border-green-200 bg-white px-2 py-1.5 text-xs">
                {minted}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(minted);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className={btnSmall}
              >
                {t('booking:eventTypes.copyLink')}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium">{t('booking:invites.sent')}</h2>
        {data?.items.length ? (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
            {data.items.map((invite) => {
              const expired = new Date(invite.expiresAt).getTime() <= Date.now();
              const state = invite.usedAt ? 'used' : expired ? 'expired' : 'open';
              return (
                <li
                  key={invite.id}
                  className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {invite.name || invite.email || t('booking:invites.anyone')}
                    <span className="ml-2 text-xs text-neutral-500">
                      {t(`booking:invites.state.${state}`, { date: fmt(invite.expiresAt) })}
                    </span>
                  </span>
                  {state === 'open' ? (
                    <button
                      type="button"
                      onClick={() => revoke.mutate(invite.id)}
                      disabled={revoke.isPending}
                      className={btnSmall}
                    >
                      {t('booking:invites.revoke')}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-neutral-500">{t('booking:invites.none')}</p>
        )}
      </section>
    </div>
  );
}
